import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { ExpandableSection } from '../dto';
import { AddonsIdentifiersEnum, FeatureFlagsEnum, SearchPanelSectionsIds, SearchPanelSubSectionsIdsEnum, SidebarTabTypeEnum } from 'src/app/shared/enums';
import { OwnedContactsService } from './owned-contacts.service';
import { map } from 'rxjs/operators';
import { FeatureFlagService } from './feature-flag.service';
import { AccountService } from './account.service';
import { UserService } from '../../core/services';
import { User } from '../../core/models/user.model';

@Injectable({ providedIn: 'root' })
export class SearchPanelStructureService {
    sections: ExpandableSection[] = [];
    visitorIntelSections: ExpandableSection[] = [];
    private currentFilterSubjects: {
        [key in SidebarTabTypeEnum]?: BehaviorSubject<SearchPanelSubSectionsIdsEnum | string>
    } = {};
    private allFiltersClearedSubject: Subject<void> = new Subject<void>();
    private initialized:boolean = false;
    private readonly crmOwnedContactsHidden$: Observable<boolean> = this.ownedContactsService.getCrmOwnedContactsHidden();
    private readonly crmOwnedContactsDisabled$: Observable<boolean> = this.ownedContactsService.getCrmOwnedContactsDisabled();

    constructor(
        private readonly ownedContactsService: OwnedContactsService,
        private accountService: AccountService,
        private readonly userService: UserService,
        private readonly featureFlagService: FeatureFlagService,
    ) {
    }

    init() {
        if (!this.initialized) {
            this.sections = this.getSections();
            this.visitorIntelSections = this.getVisitorIntelSections();
            this.initialized = true;
        }
    }

    getCurrentFilterEvent(panel: SidebarTabTypeEnum): Observable<SearchPanelSubSectionsIdsEnum | string> {
        if (!this.currentFilterSubjects[panel]) {
            this.currentFilterSubjects[panel] = new BehaviorSubject<string>('')
        }
        return this.currentFilterSubjects[panel].asObservable();
    }

    emitCurrentFilterEvent(filterId: SearchPanelSubSectionsIdsEnum | string, panel: SidebarTabTypeEnum): void {
        this.currentFilterSubjects[panel]?.next(filterId);
    }

    getAllFiltersClearedEvent(): Observable<void> {
        return this.allFiltersClearedSubject.asObservable();
    }

    emitAllFiltersClearedEvent(): void {
        this.allFiltersClearedSubject.next();
    }

    deselectAllFilters(panel: SidebarTabTypeEnum): void {
        if (panel === 'SalesIntel') {
            this.sections.forEach((section) => {
                const selectedFilter = section.list.find((filter) => filter.selected);
                if (selectedFilter) {
                    selectedFilter.selected = false;
                }
            });
        }
        if (panel === 'VisitorIntel') {
            this.visitorIntelSections.forEach((section) => {
                const selectedFilter = section.list.find((filter) => filter.selected);
                if (selectedFilter) {
                    selectedFilter.selected = false;
                }
            });
        }
    }

    getVisitorIntelSections(): ExpandableSection[] {
        const sections: ExpandableSection[] = [
            {   id: SearchPanelSectionsIds.VI_PAGE_FILTERS,
                title: 'Page filters',
                expanded: false,
                list: [
                    {id: 'page_filters_include', title: 'Include',  references: [
                        'page_filters/include'
                    ], selected: false},
                    {id: 'page_filters_exclude', title: 'Exclude',  references: [
                        'page_filters/exclude'
                    ], selected: false},
                ],
            },
            {   id: SearchPanelSectionsIds.VI_COMPANY_PROFILE,
                title: 'Company Profile',
                expanded: false,
                list: [
                    {id: 'company_location', title: 'Company Location',  references: [
                        'company_location_states',
                        'company_location_zipcodes',
                        'company_location_metro',
                        'location_country_codes',
                        'company_location_branch',
                        'company_location_hq'
                    ], selected: false},
                    {id: 'company_keywords', title: 'Keywords',  references: [
                        ['specialties', 'specialties_any', 'specialties_bool'],
                        'specialties_all',
                        'specialties_none',
                        'popular_keywords',
                    ], selected: false},
                    {id: 'company_industry', title: 'Industry',  references: [
                        ['company_naics_codes', 'company_naics_codes_any', 'company_naics_codes_bool'],
                        'company_naics_codes_all',
                        'company_naics_codes_none',
                        ['company_sic_codes', 'company_sic_codes_any', 'company_sic_codes_bool'],
                        'company_sic_codes_all',
                        'company_sic_codes_none',
                        'include_primary_industry_only'
                    ], selected: false},
                    {id: 'company_revenue', title: 'Revenue',  references: [
                        'company_min_revenue',
                        'company_max_revenue',
                    ], selected: false},
                    {id: 'company_employees_size', title: 'Employees / Size',  references: [
                        'company_min_size',
                        'company_max_size',
                    ], selected: false},
                    {id: 'company_technographic_products', title: 'Tech Products',  references: [
                        ['company_technographic_products', 'company_technographic_products_any', 'company_technographic_products_bool'],
                        'company_technographic_products_all',
                        'company_technographic_products_none',
                    ], selected: false},
                    {id: 'company_technographic_vendors', title: 'Tech Providers',  references: [
                        ['company_technographic_vendors', 'company_technographic_vendors_any', 'company_technographic_vendors_bool'],
                        'company_technographic_vendors_all',
                        'company_technographic_vendors_none',
                    ], selected: false},
                    {id: 'company_technographic_categories', title: 'Tech Categories',  references: [
                        ['company_technographic_categories', 'company_technographic_categories_any', 'company_technographic_categories_bool'],
                        'company_technographic_categories_all',
                        'company_technographic_categories_none',
                        ['company_technographic_sub_categories', 'company_technographic_sub_categories_any', 'company_technographic_sub_categories_bool'],
                        'company_technographic_sub_categories_all',
                        'company_technographic_sub_categories_none'
                    ], selected: false},
                ],
            },
            {   id: SearchPanelSectionsIds.VI_IN_MARKET_SIGNALS,
                title: 'In-Market Signals',
                expanded: false,
                list: [
                    {id: 'intent_topics', title: 'Intent',  references: [
                        'si_intent_topic_ids',
                        'intent_topic_ids',
                        'intent_minimum_score',
                        'intent_maximum_score',
                        'intent_topic_threshold',
                    ], selected: false},
                ],
            },
            {   id: SearchPanelSectionsIds.VI_MORE,
                title: 'Additional Filters',
                expanded: false,
                list: [
                    {
                        id: SearchPanelSubSectionsIdsEnum.YourLists,
                        title: 'Your Lists',
                        references: ['contact_lists', 'lists', 'rodId', 'suppression_lists'],
                        selected: false,
                    },
                ],
            },
        ];

        return [this.getSummarySection(sections), ...sections];
    }

    getSections(): ExpandableSection[] {
        const sections: ExpandableSection[] = [
            {   id: SearchPanelSectionsIds.COMPANY_PROFILE,
                title: 'Company Profile',
                expanded: false,
                list: [
                    {id: 'company_domains_names', title: 'Company',  references: [
                        ['company_domains', 'company_names'],
                    ] , selected: false},

                    {id: 'company_location', title: 'Company Location',  references: [
                        'company_location_states',
                        'company_location_zipcodes',
                        'company_location_metro',
                        'location_country_codes',
                        'company_location_branch',
                        'company_location_hq'
                    ], selected: false},

                    {id: 'company_keywords', title: 'Keywords',  references: [
                        ['specialties', 'specialties_any', 'specialties_bool'],
                        'specialties_all',
                        'specialties_none',
                        'popular_keywords',

                    ], selected: false},

                    {id: 'company_industry', title: 'Industry',  references: [
                        ['company_naics_codes', 'company_naics_codes_any', 'company_naics_codes_bool'],
                        'company_naics_codes_all',
                        'company_naics_codes_none',
                        ['company_sic_codes', 'company_sic_codes_any', 'company_sic_codes_bool'],
                        'company_sic_codes_all',
                        'company_sic_codes_none',
                        'include_primary_industry_only'
                    ], selected: false},

                    {id: 'company_revenue', title: 'Revenue',  references: [
                        'company_min_revenue',
                        'company_max_revenue',
                    ], selected: false},

                    {id: 'company_employees_size', title: 'Employees / Size',  references: [
                        'company_min_size',
                        'company_max_size',
                    ], selected: false},

                    {id: 'company_department_size', title: 'Department Size',  references: [
                        'department_size'
                    ], selected: false},



                    {id: 'company_technographic_products', title: 'Tech Product',  references: [
                        ['company_technographic_products', 'company_technographic_products_any', 'company_technographic_products_bool'],
                        'company_technographic_products_all',
                        'company_technographic_products_none',
                    ], selected: false},

                    {id: 'company_technographic_vendors', title: 'Tech Provider',  references: [
                        ['company_technographic_vendors', 'company_technographic_vendors_any', 'company_technographic_vendors_bool'],
                        'company_technographic_vendors_all',
                        'company_technographic_vendors_none',
                    ], selected: false},

                    {id: 'company_technographic_categories', title: 'Tech Category',  references: [
                        ['company_technographic_categories', 'company_technographic_categories_any', 'company_technographic_categories_bool'],
                        'company_technographic_categories_all',
                        'company_technographic_categories_none',
                        ['company_technographic_sub_categories', 'company_technographic_sub_categories_any', 'company_technographic_sub_categories_bool'],
                        'company_technographic_sub_categories_all',
                        'company_technographic_sub_categories_none'
                    ], selected: false},

                    {id: 'company_tech_spend', title: 'Tech Spend', references: [
                            'it_spend_super_category', 'it_spend_category', 'it_spend_sub_category', 'min_it_spend_amount', 'max_it_spend_amount'
                        ], selected: false, hidden$: this.featureFlagService.getFlag(FeatureFlagsEnum.TechSpendFilter).pipe(map(flag => !flag))},

                    {id: 'company_funding', title: 'Funding',  references: [
                        'min_last_funding_date', 'max_last_funding_date', 'last_funding_rounds', 'min_funding_amount', 'max_funding_amount', 'last_funding_date',
                    ], selected: false},
                ],
            },
            {   id: SearchPanelSectionsIds.IN_MARKET_SIGNALS,
                title: 'In-Market Signals',
                expanded: false,
                list: [
                    {id: 'intent_topics', title: 'Intent',  references: [
                        'si_intent_topic_ids',
                        'intent_topic_ids',
                        'intent_minimum_score',
                        'intent_maximum_score',
                        'intent_topic_threshold',
                    ], selected: false},
                    {id: 'visitorintel',
                        title: 'VisitorIntel',
                        references: [
                            'viDateFrom',
                            'viDateTo',
                            'viSearchDurationType',
                        ],
                        selected: false,
                        tooltipDisabled: 'You do not have VisitorIntel in your current plan.  Contact support@salesintel.io to learn how to add it.',
                        disabled$: of(!this.accountService.hasPlans([AddonsIdentifiersEnum.VISITORINTEL])),
                    },
                ],
            },



            {   id: SearchPanelSectionsIds.BUYER_PERSONA,
                title: 'Buyer Persona',
                expanded: false,
                list: [
                    {id: 'person_job_level', title: 'Level',  references: ['job_levels'], selected: false},

                    {id: 'person_job_department', title: 'Department',  references: [
                        'job_departments',
                        'job_sub_departments'
                    ], selected: false},

                    {id: 'person_job_title', title: 'Title',  references: [
                        ['job_title', 'job_title_any', 'job_title_bool'],
                        'job_title_all',
                        'job_title_none',
                    ], selected: false},

                    {id: 'person_name', title: 'Name',  references: ['first_name', 'last_name'], selected: false},

                    {id: 'person_email', title: 'Email',  references: ['email'], selected: false},

                    {
                        id: SearchPanelSubSectionsIdsEnum.JobStartDate,
                        title: 'Job Start Date',
                        references: ['job_start_date'],
                        selected: false,
                        disabled$: this.userService.getUserData().pipe(map((user: User) => !user.job_change_enabled || user.job_change_freemium)),
                        tooltipDisabled: '<span>Email <a href="mailto:support@salesintel.io">support@salesintel.io</a> to add the Job Start Date filter to your account.</span>',
                    },

                    {id: 'person_location', title: 'Contact Location',  references: [
                        'contact_location_states',
                        'contact_location_zipcodes',
                        'contact_location_metro',
                        'location_country_codes',
                        'location_types'
                    ], selected: false},
                ],
            },
            {   id: SearchPanelSectionsIds.MORE,
                title: 'Additional Filters',
                expanded: false,
                list: [
                    {
                        id: SearchPanelSubSectionsIdsEnum.CrmOwnedAccounts,
                        title: 'CRM Accounts',
                        references: ['owned_contacts_company/include', 'owned_contacts_company/exclude'],
                        selected: false,
                        tooltipDisabled: 'This filter will be available once your CRM data has finished loading.',
                        hidden$: this.crmOwnedContactsHidden$,
                        disabled$: this.crmOwnedContactsDisabled$,
                    },
                    {
                        id: SearchPanelSubSectionsIdsEnum.CrmOwnedContacts,
                        title: 'CRM Contacts',
                        references: ['owned_contacts_person/include', 'owned_contacts_person/exclude'],
                        selected: false,
                        tooltipDisabled: 'This filter will be available once your CRM data has finished loading.',
                        hidden$: this.crmOwnedContactsHidden$,
                        disabled$: this.crmOwnedContactsDisabled$,
                    },
                    {
                      id: SearchPanelSubSectionsIdsEnum.ICPIntelModel,
                      title: 'ICPIntel Model',
                      references: ['icpintel_job_uuid'],
                      selected: false,
                      hidden$: this.featureFlagService.getFlag(FeatureFlagsEnum.ICPIntelV2Model).pipe(map(flag => !flag))
                    },
                    {
                        id: SearchPanelSubSectionsIdsEnum.AccuracyLevel,
                        title: 'Level of Accuracy',
                        references: ['accuracy_tier_company', 'accuracy_tier_contact'],
                        selected: false,
                        disableCounter: true,
                    },
                    {
                        id: SearchPanelSubSectionsIdsEnum.Rankings,
                        title: 'Rankings',
                        references: ['rankings'],
                        selected: false,
                    },
                    {
                        id: SearchPanelSubSectionsIdsEnum.YourLists,
                        title: 'Your Lists',
                        references: ['contact_lists', 'lists', 'rodId', 'suppression_lists'],
                        selected: false,
                    },
                    {
                        id: SearchPanelSubSectionsIdsEnum.ResultsInclude,
                        title: 'Results Include',
                        references: ['required_fields'],
                        selected: false,
                    },
                    {
                        id: SearchPanelSubSectionsIdsEnum.LastModifiedDate,
                        title: 'Last Modified',
                        references: ['gte_last_modified_date'],
                        selected: false,
                    },
                ],
            },
        ];

        return [this.getSummarySection(sections), ...sections ];
    }

    getSummarySection(sections: ExpandableSection[]): ExpandableSection {
        const allFilters = sections.reduce((result, section) =>
            section.list.length ? [...result,
                ...(section.list.map((filter) => ({...filter})))] : result, []);

        let locationIndex = -1;
        allFilters.forEach((filter, index) => {
            if (filter.id === 'company_location') {
                locationIndex = index;
            }
        });
        if (locationIndex > -1){
            allFilters.splice(locationIndex, 0, {id: 'location', title: 'Location', references: ['location_country_codes'], selected: false});
        }

        let allReferences = sections.reduce((result, section) => [...result,
            ...section.list.reduce((references, filter) => !filter.disableCounter ? [...references, ...filter.references] : references, [])
        ], []);

        allReferences = Array.from(new Set(allReferences));

        const fullSummary = [
            {id: 'all', title: 'All',  references: allReferences , selected: false}
        ,...allFilters];

        return {
            id: SearchPanelSectionsIds.SUMMARY,
            title: 'Applied Filters',
            expanded: false,
            list: [...fullSummary]
        }
    }
}
